import { type JSX, useState } from 'react';

import {
  auskunft as holeAuskunft,
  einschraenkung as setzeEinschraenkung,
  loeschpruefung as holeLoeschpruefung,
  personen as suchePersonen,
  werbestandAmAnsprechpartnerSetzen,
  werbestandSetzen,
  WERBESTANDNAME,
  type Firmenstand,
  type Loeschbefund,
  type Personenzeile,
  type Werbestand,
} from '../api/datenschutz';
import { ApiFehler } from '../api/fehler';
import { markenklasse } from '../marken';
import { Reiterleiste, type Reiterwahl } from '../komponenten/Akte';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';

/**
 * Die Betroffenenrechte (LEG-10, LEG-11, LEG-12).
 *
 * ============================================================================
 * EINE MASKE FÜR DREI ANLIEGEN, WEIL SIE ZUSAMMEN KOMMEN
 * ============================================================================
 *
 * Wer schreibt, „bitte löschen Sie meine Daten", will drei Dinge zugleich:
 * wissen, was gespeichert ist (Art. 15), dass es weg kommt (Art. 17) und dass
 * keine Werbung mehr kommt (Art. 21). Drei Masken dafür hießen, denselben
 * Brief dreimal zu bearbeiten.
 *
 * ============================================================================
 * DIE SUCHE BEGINNT NICHT VON SELBST
 * ============================================================================
 *
 * Eine Liste aller Personen wäre selbst eine Verarbeitung ohne Anlass. Der
 * Anlass ist das Ersuchen einer bestimmten Person — deshalb erst ein Name,
 * dann eine Liste.
 *
 * ============================================================================
 * WAS DIESE MASKE NICHT KANN
 * ============================================================================
 *
 * Löschen. Die Löschprüfung sagt, was einer Löschung entgegensteht und bis
 * wann (LEG-11); wo eine Aufbewahrungspflicht besteht, wird die Verarbeitung
 * eingeschränkt statt gelöscht. Das tatsächliche Löschen greift in
 * festgeschriebene Belege ein und läuft über das Aufbewahrungsmodell
 * (LOG-12), nicht über einen Knopf.
 */

/** Die Reiter der gewählten Person — je Anliegen einer. */
type Anliegen = 'auskunft' | 'loeschung' | 'werbung';

function tag(wert: string | null): string {
  if (wert === null) return '—';
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? wert : d.toLocaleDateString('de-DE');
}

function name(p: Personenzeile): string {
  return p.vorname === null ? p.nachname : `${p.nachname}, ${p.vorname}`;
}

export function Datenschutz({
  darfAuskunft,
  darfEinschraenken,
  darfWerbestand,
}: {
  /** `betroffenenrecht:auskunft` — Art. 15 und die Löschprüfung. */
  readonly darfAuskunft: boolean;
  /** `betroffenenrecht:einschraenken` — Art. 18. */
  readonly darfEinschraenken: boolean;
  /** `kunde:aendern` — der Werbewiderspruch kommt am Telefon an. */
  readonly darfWerbestand: boolean;
}): JSX.Element {
  const [suche, setzeSuche] = useState('');
  const [treffer, setzeTreffer] = useState<readonly Personenzeile[] | null>(null);
  const [gewaehlt, setzeGewaehlt] = useState<Personenzeile | null>(null);
  const [auskunft, setzeAuskunft] = useState<Record<string, unknown> | null>(null);
  const [befunde, setzeBefunde] = useState<readonly Loeschbefund[] | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laedt, setzeLaedt] = useState(false);
  const [anliegen, setzeAnliegen] = useState<Anliegen>('auskunft');

  const [werbestand, setzeWerbestand] = useState<Werbestand>('widerspruch');
  const [quelle, setzeQuelle] = useState('');
  /** LEG-12 (0319): wofür der Stand gilt — 'privat' oder eine Kontaktrolle. */
  const [werbeziel, setzeWerbeziel] = useState('privat');
  const [einschraenkungsgrund, setzeEinschraenkungsgrund] = useState('');
  const [nachfrage, setzeNachfrage] = useState<'einschraenken' | 'freigeben' | null>(null);

  async function handgriff(tun: () => Promise<unknown>, sagen?: string): Promise<void> {
    setzeMeldung(null);
    setzeLaedt(true);
    try {
      await tun();
      if (sagen !== undefined) setzeMeldung(sagen);
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }

  /*
   * Die Rückfrage (CRM-104) läuft an `handgriff` vorbei: Ihr Fehler gehört ins
   * Pop-up. Erst nach dem Erfolg schließt sie, und die Liste lädt neu.
   */
  async function bestaetigt(person: string, grund: string | null, sagen: string): Promise<void> {
    await setzeEinschraenkung(person, grund);
    setzeNachfrage(null);
    setzeFehler(null);
    void handgriff(() => neuLaden(person), sagen);
  }

  /** Nach jedem Handgriff neu laden — der Stand steht in der Trefferliste. */
  async function neuLaden(person: string): Promise<void> {
    const liste = await suchePersonen(suche.trim());
    setzeTreffer(liste);
    setzeGewaehlt(liste.find((p) => p.id === person) ?? null);
  }

  async function waehle(p: Personenzeile): Promise<void> {
    setzeGewaehlt(p);
    setzeAuskunft(null);
    setzeBefunde(null);
    setzeQuelle('');
    setzeEinschraenkungsgrund(p.einschraenkungsgrund ?? '');
    setzeWerbeziel('privat');
    if (p.werbestand !== null) setzeWerbestand(p.werbestand);
  }

  const reiterliste: readonly Reiterwahl<Anliegen>[] = [
    { kennung: 'auskunft', beschriftung: 'Auskunft (Art. 15)' },
    { kennung: 'loeschung', beschriftung: 'Löschung und Einschränkung (Art. 17, 18)' },
    { kennung: 'werbung', beschriftung: 'Werbung (Art. 21)' },
  ];

  return (
    <div className="stapel">
      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {/*
        SEITENTYP EINSTELLUNGEN (ÄA-41, Seitenplan): Oben die Suche nach der
        Person, darunter je Anliegen ein Reiter und je Thema eine Karte. Die
        drei Anliegen bleiben in EINER Maske — der Reiter wechselt, die Person
        bleibt gewählt.
      */}
      <Einstellungsseite>
        <Thema titel="Person suchen" breit>
          <p className="hinweiszeile">
            Auskunft nach Art. 15, Einschränkung der Verarbeitung nach Art. 18 und
            Werbewiderspruch nach Art. 21. Gegenstand ist die{' '}
            <strong>Person</strong> — dieselbe kann Kunde, Ansprechpartner und Teilnehmer
            zugleich sein.
          </p>

          <form
            className="liste__leiste"
            onSubmit={(e) => {
              e.preventDefault();
              void handgriff(async () => {
                setzeGewaehlt(null);
                setzeAuskunft(null);
                setzeBefunde(null);
                setzeTreffer(await suchePersonen(suche.trim()));
              });
            }}
          >
            <label className="liste__filter">
              Name{' '}
              <input
                type="search"
                aria-label="Name der Person"
                value={suche}
                onChange={(e) => {
                  setzeSuche(e.target.value);
                }}
              />
            </label>{' '}
            <button type="submit" className="knopf knopf--haupt" disabled={suche.trim().length < 2}>
              Suchen
            </button>
          </form>

          {treffer !== null && treffer.length === 0 && (
            <p className="hinweiszeile">Zu diesem Namen ist niemand gespeichert.</p>
          )}

          {treffer !== null && treffer.length > 0 && (
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle" aria-label="Gefundene Personen">
                <thead>
                  <tr>
                    <th scope="col">Person</th>
                    <th scope="col">Geboren</th>
                    <th scope="col">Vorkommen</th>
                    <th scope="col">Stand</th>
                    <th scope="col">Handlung</th>
                  </tr>
                </thead>
                <tbody>
                  {treffer.map((p) => (
                    <tr
                      key={p.id}
                      className={gewaehlt?.id === p.id ? 'liste__zeile--gewaehlt' : undefined}
                    >
                      <th scope="row">{name(p)}</th>
                      <td className="liste__einzeilig">{tag(p.geburtsdatum)}</td>
                      <td>
                        {p.rollen.length === 0 ? (
                          <span className="hilfe">nur als Person gespeichert</span>
                        ) : (
                          p.rollen.join(' · ')
                        )}
                      </td>
                      <td>
                        {p.eingeschraenktAb !== null && (
                          <span className={markenklasse('sackgasse')}>Verarbeitung eingeschränkt</span>
                        )}{' '}
                        {/* LEG-12: der PRIVATE Stand; die Stände je Firma stehen beim Bearbeiten. */}
                        {p.werbestand === 'widerspruch' ? (
                          <span className={markenklasse('aufmerksamkeit')}>Werbewiderspruch</span>
                        ) : p.werbestand === null ? (
                          <span className="hilfe">keine Werbeerlaubnis</span>
                        ) : (
                          <span className={markenklasse('abschluss')}>
                            {WERBESTANDNAME[p.werbestand]}
                          </span>
                        )}
                        {(p.firmenstaende ?? []).some((f) => f.wirksam === 'widerspruch') && (
                          <>
                            {' '}
                            <span className={markenklasse('aufmerksamkeit')}>
                              Widerspruch als Ansprechpartner
                            </span>
                          </>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--verweis"
                          onClick={() => void waehle(p)}
                        >
                          Bearbeiten
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Thema>
      </Einstellungsseite>

      {gewaehlt !== null && (
        <>
          {/* LOG-02, LOG-11 */}
          <p className="hinweiszeile">
            <strong>{name(gewaehlt)}</strong> — jeder Handgriff wird protokolliert, auch die
            Auskunft selbst ist eine Verarbeitung.
          </p>

          <Reiterleiste
            beschriftung={`Anliegen von ${name(gewaehlt)}`}
            reiter={reiterliste}
            gewaehlt={anliegen}
            beiWahl={setzeAnliegen}
            praefix="ds-"
          />

          <div
            className="einstellungen"
            role="tabpanel"
            id={`tafel-ds-${anliegen}`}
            aria-labelledby={`reiter-ds-${anliegen}`}
          >
            {/* ================================================================
                Art. 15 — die Auskunft (LEG-10)
                ================================================================ */}
            {anliegen === 'auskunft' && (
              <Thema titel="Auskunft nach Art. 15 DSGVO" breit>
                {darfAuskunft ? (
                  <>
                    <div className="knopfreihe">
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        disabled={laedt}
                        onClick={() =>
                          void handgriff(async () => {
                            setzeAuskunft(await holeAuskunft(gewaehlt.id));
                          })
                        }
                      >
                        Auskunft erstellen
                      </button>
                    </div>
                    {auskunft !== null && (
                      <>
                        <p className="feld__hilfe">
                          Alles, was zu dieser Person gespeichert ist. Preise und
                          Deckungsbeiträge stehen nicht darin — sie betreffen das Geschäft und
                          nicht die Person.
                        </p>
                        <pre className="quelltext" aria-label="Auskunft">
                          {JSON.stringify(auskunft, null, 2)}
                        </pre>
                      </>
                    )}
                  </>
                ) : (
                  <p className="hilfe">
                    Dafür fehlt das Recht „Auskunft erteilen" (betroffenenrecht:auskunft).
                  </p>
                )}
              </Thema>
            )}

            {/* ================================================================
                Art. 17/18 — Löschprüfung und Einschränkung (LEG-11)
                ================================================================ */}
            {anliegen === 'loeschung' && (
              <>
                <Thema titel="Löschprüfung" zusatz="Art. 17" breit>
                  {darfAuskunft ? (
                    <div className="knopfreihe">
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        disabled={laedt}
                        onClick={() =>
                          void handgriff(async () => {
                            setzeBefunde(await holeLoeschpruefung(gewaehlt.id));
                          })
                        }
                      >
                        Prüfen
                      </button>
                    </div>
                  ) : (
                    <p className="hilfe">
                      Dafür fehlt das Recht „Auskunft erteilen" (betroffenenrecht:auskunft).
                    </p>
                  )}
                  {befunde !== null && (
                    <div className="tabellenhuelle tabellenhuelle--telefon">
                      <table className="liste__tabelle" aria-label="Löschprüfung">
                        <thead>
                          <tr>
                            <th scope="col">Gegenstand</th>
                            <th scope="col">Anzahl</th>
                            <th scope="col">Befund</th>
                            <th scope="col">Grundlage</th>
                            <th scope="col">Aufzubewahren bis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {befunde.map((b) => (
                            <tr key={b.gegenstand}>
                              <th scope="row">{b.gegenstand}</th>
                              <td className="zahl">{b.anzahl}</td>
                              <td>
                                {b.entscheidung === 'loeschbar' ? (
                                  <span className={markenklasse('abschluss')}>löschbar</span>
                                ) : (
                                  <span className={markenklasse('aufmerksamkeit')}>
                                    Aufbewahrungspflicht
                                  </span>
                                )}
                              </td>
                              <td>{b.grundlage}</td>
                              <td>
                                {/* Die Frist in Rost (LEG-11): bis dahin wird eingeschränkt statt gelöscht. */}
                                {b.bis === null ? (
                                  '—'
                                ) : (
                                  <span className={markenklasse('aufmerksamkeit')}>{tag(b.bis)}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Thema>

                <Thema titel="Einschränkung der Verarbeitung (Art. 18)" breit>
                  {/* KUN-17 */}
                  <p className="feld__hilfe">
                    Wo nicht gelöscht werden darf, wird die Verarbeitung eingeschränkt. Das ist{' '}
                    <strong>nicht die Kundensperre</strong>: Die hängt am Kunden und ist eine
                    geschäftliche Entscheidung.
                  </p>
                  {gewaehlt.eingeschraenktAb !== null && (
                    <p className="hinweiszeile">
                      <span className={markenklasse('sackgasse')}>eingeschränkt</span> seit{' '}
                      {tag(gewaehlt.eingeschraenktAb)}: {gewaehlt.einschraenkungsgrund}
                    </p>
                  )}
                  {darfEinschraenken ? (
                    <div className="liste__leiste">
                      <label className="liste__filter">
                        Grund{' '}
                        <input
                          aria-label="Grund der Einschränkung"
                          value={einschraenkungsgrund}
                          onChange={(e) => {
                            setzeEinschraenkungsgrund(e.target.value);
                          }}
                        />
                      </label>{' '}
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        disabled={laedt || einschraenkungsgrund.trim() === ''}
                        onClick={() => setzeNachfrage('einschraenken')}
                      >
                        Einschränken …
                      </button>{' '}
                      {gewaehlt.eingeschraenktAb !== null && (
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={laedt}
                          onClick={() => setzeNachfrage('freigeben')}
                        >
                          Wieder freigeben …
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="hilfe">
                      Dafür fehlt das Recht „Verarbeitung einschränken"
                      (betroffenenrecht:einschraenken).
                    </p>
                  )}

                  {/* LEG-11: Der Grund steht schon im Feld — das Pop-up zeigt ihn, fragt ihn nicht neu. */}
                  {darfEinschraenken && nachfrage === 'einschraenken' && (
                    <Rueckfrage
                      frage="Verarbeitung einschränken?"
                      unterzeile={name(gewaehlt)}
                      tat="Einschränken"
                      beiSchliessen={() => setzeNachfrage(null)}
                      beiBestaetigen={() =>
                        bestaetigt(
                          gewaehlt.id,
                          einschraenkungsgrund.trim(),
                          'Die Verarbeitung ist eingeschränkt.',
                        )
                      }
                    >
                      <p>
                        Die Daten bleiben gespeichert, werden aber nur noch eingeschränkt
                        verarbeitet
                        {gewaehlt.eingeschraenktAb === null
                          ? ''
                          : ` — die Einschränkung seit ${tag(gewaehlt.eingeschraenktAb)} erhält den neuen Grund und das heutige Datum`}
                        .
                      </p>
                      <p>Grund: {einschraenkungsgrund.trim()}</p>
                    </Rueckfrage>
                  )}

                  {darfEinschraenken &&
                    nachfrage === 'freigeben' &&
                    gewaehlt.eingeschraenktAb !== null && (
                      <Rueckfrage
                        frage="Einschränkung aufheben?"
                        unterzeile={name(gewaehlt)}
                        tat="Freigeben"
                        beiSchliessen={() => setzeNachfrage(null)}
                        beiBestaetigen={() =>
                          bestaetigt(gewaehlt.id, null, 'Die Einschränkung ist aufgehoben.')
                        }
                      >
                        <p>
                          Die Daten werden wieder uneingeschränkt verarbeitet; eingeschränkt seit{' '}
                          {tag(gewaehlt.eingeschraenktAb)}: {gewaehlt.einschraenkungsgrund}. Hat die
                          Person die Einschränkung verlangt, ist sie vor der Aufhebung zu
                          unterrichten (Art. 18 Abs. 3 DSGVO).
                        </p>
                      </Rueckfrage>
                    )}
                </Thema>
              </>
            )}

            {/* ================================================================
                Art. 21 — der Werbewiderspruch (LEG-12)
                ================================================================ */}
            {anliegen === 'werbung' && (
              <Thema
                titel="Werbliche Ansprache"
                zusatz={
                  gewaehlt.werbestand === null ? (
                    'privat: keine Werbeerlaubnis'
                  ) : (
                    <span
                      className={markenklasse(
                        gewaehlt.werbestand === 'widerspruch' ? 'aufmerksamkeit' : 'abschluss',
                      )}
                    >
                      privat: {WERBESTANDNAME[gewaehlt.werbestand]}
                    </span>
                  )
                }
                breit
              >
                <p className="feld__hilfe">
                  Geworben wird nur bei dokumentierter Einwilligung oder zulässigem
                  Ausnahmetatbestand. <strong>Ein Widerspruch wirkt kanalübergreifend</strong> —
                  er gilt für Post, Mail und Telefon zugleich.
                </p>
                <Werbestaende
                  privat={gewaehlt.werbestand}
                  privatkunde={gewaehlt.rollen.some((r) => r.startsWith('Kunde '))}
                  firmen={gewaehlt.firmenstaende ?? []}
                />
                <p className="feld__hilfe">
                  Der <strong>private</strong> Stand gilt nur privat. Als Ansprechpartner hat die
                  Person je Firma einen eigenen Stand; ein Widerspruch der Firma gilt dort für alle
                  Ansprechpartner.
                </p>
                {darfWerbestand ? (
                  <div className="liste__leiste">
                    {(gewaehlt.firmenstaende ?? []).length > 0 && (
                      <>
                        <label className="liste__filter">
                          Gilt für{' '}
                          <select
                            aria-label="Werbestand gilt für"
                            value={werbeziel}
                            onChange={(e) => {
                              setzeWerbeziel(e.target.value);
                            }}
                          >
                            <option value="privat">privat</option>
                            {(gewaehlt.firmenstaende ?? [])
                              .filter((f) => !f.ausgeschieden)
                              .map((f) => (
                                <option key={f.kontaktrolle} value={f.kontaktrolle}>
                                  Ansprechpartner bei {firmenname(f)}
                                </option>
                              ))}
                          </select>
                        </label>{' '}
                      </>
                    )}
                    <label className="liste__filter">
                      Stand{' '}
                      <select
                        aria-label="Werbestand"
                        value={werbestand}
                        onChange={(e) => {
                          setzeWerbestand(e.target.value as Werbestand);
                        }}
                      >
                        {(['einwilligung', 'ausnahme', 'widerspruch'] as const).map((s) => (
                          <option key={s} value={s}>
                            {WERBESTANDNAME[s]}
                          </option>
                        ))}
                      </select>
                    </label>{' '}
                    <label className="liste__filter">
                      Quelle{' '}
                      <input
                        aria-label="Quelle des Werbestands"
                        placeholder="Anruf vom 09.09., Frau Muster"
                        value={quelle}
                        onChange={(e) => {
                          setzeQuelle(e.target.value);
                        }}
                      />
                    </label>{' '}
                    {/* LEG-12: „Widerspruch" allein ist im Streitfall keine Auskunft. */}
                    <button
                      type="button"
                      className="knopf knopf--klein"
                      disabled={laedt || quelle.trim() === ''}
                      onClick={() =>
                        void handgriff(async () => {
                          if (werbeziel === 'privat') {
                            await werbestandSetzen(gewaehlt.id, werbestand, quelle.trim());
                          } else {
                            await werbestandAmAnsprechpartnerSetzen(
                              werbeziel,
                              werbestand,
                              quelle.trim(),
                            );
                          }
                          await neuLaden(gewaehlt.id);
                          setzeQuelle('');
                        }, 'Der Werbestand ist vermerkt.')
                      }
                    >
                      Vermerken
                    </button>
                  </div>
                ) : (
                  <p className="hilfe">Dafür fehlt das Recht „Kunden ändern" (kunde:aendern).</p>
                )}
              </Thema>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function firmenname(f: Firmenstand): string {
  return `${f.firma ?? 'Kunde'} (${f.kundennummer})`;
}

/**
 * Die Werbestände einer Person, getrennt nach Zusammenhang (LEG-12, 0319).
 *
 * Privat und je Firma ein eigener Stand. „Gilt dort" ist der wirksame Stand —
 * ein Widerspruch der Firma geht dem eigenen vor. Die Regel steht in der
 * Datenbank (`fach.werbestand_wirksam`); hier steht nur ihr Ergebnis.
 */
function Werbestaende({
  privat,
  privatkunde,
  firmen,
}: {
  readonly privat: Werbestand | null;
  readonly privatkunde: boolean;
  readonly firmen: readonly Firmenstand[];
}): JSX.Element {
  const name = (s: Werbestand | null): string => (s === null ? '—' : WERBESTANDNAME[s]);
  return (
    <div className="tabellenhuelle">
      <table className="liste__tabelle" aria-label="Werbestände der Person">
        <thead>
          <tr>
            <th scope="col">Zusammenhang</th>
            <th scope="col">Eigener Stand</th>
            <th scope="col">Gilt dort</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{privatkunde ? 'privat (eigenes Kundenkonto)' : 'privat'}</td>
            <td>{name(privat)}</td>
            <td>
              {privat === null ? (
                <span className="hilfe">keine Werbeerlaubnis</span>
              ) : (
                <span className={markenklasse(privat === 'widerspruch' ? 'aufmerksamkeit' : 'abschluss')}>
                  {WERBESTANDNAME[privat]}
                </span>
              )}
            </td>
          </tr>
          {firmen.map((f) => (
            <tr key={f.kontaktrolle}>
              <td>
                Ansprechpartner bei {firmenname(f)}
                {f.ausgeschieden && (
                  <>
                    {' '}
                    <span className="marke marke--inaktiv">ausgeschieden</span>
                  </>
                )}
              </td>
              <td>{name(f.eigener)}</td>
              <td>
                {f.wirksam === null ? (
                  <span className="hilfe">keine Werbeerlaubnis</span>
                ) : (
                  <span
                    className={markenklasse(f.wirksam === 'widerspruch' ? 'aufmerksamkeit' : 'abschluss')}
                  >
                    {WERBESTANDNAME[f.wirksam]}
                    {f.von === 'firma' ? ' (Firma)' : ''}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
